import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Upload, FileArchive, Download, Trash2, LayoutTemplate } from 'lucide-react';

const API = process.env.REACT_APP_API_URL;
const MUTED = '#6b7280';

const inp = {
    background: 'var(--bg-secondary)', border: '2px solid var(--border)',
    borderRadius: '10px', color: 'var(--text-primary)', fontFamily: 'inherit',
    fontSize: '14px', padding: '10px 14px', width: '100%', boxSizing: 'border-box', outline: 'none',
};

export default function Templates() {
    const { user } = useAuth();
    const token = user?.token;

    const [templates, setTemplates] = useState([]);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [dragOver, setDragOver] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [uploadError, setUploadError] = useState('');

    const getHeaders = useCallback(() => ({ Authorization: `Bearer ${token}` }), [token]);

    const fetchTemplates = useCallback(() => {
        fetch(`${API}/api/templates`, { headers: getHeaders() })
            .then(r => r.json())
            .then(d => setTemplates(Array.isArray(d) ? d : []))
            .catch(() => {});
    }, [getHeaders]);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    const pickFile = (file) => {
        if (!file) return;
        setUploadError('');
        setSelectedFile(file);
    };

    const upload = async () => {
        if (!title.trim()) { setUploadError('Bitte einen Titel angeben.'); return; }
        if (!selectedFile) { setUploadError('Bitte eine Datei auswählen.'); return; }
        setUploading(true);
        setUploadError('');
        const formData = new FormData();
        formData.append('title', title.trim());
        formData.append('description', description.trim());
        formData.append('file', selectedFile);
        try {
            const res = await fetch(`${API}/api/templates/upload`, {
                method: 'POST', headers: getHeaders(), body: formData,
            });
            const data = await res.json();
            if (!res.ok) throw new Error(data.detail || 'Upload fehlgeschlagen');
            setTitle(''); setDescription(''); setSelectedFile(null);
            fetchTemplates();
        } catch (err) {
            setUploadError(err.message);
        }
        setUploading(false);
    };

    const download = (t) => {
        fetch(`${API}/api/templates/${t.id}/download`, { headers: getHeaders() })
            .then(r => r.blob())
            .then(blob => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = t.filename;
                document.body.appendChild(a); a.click(); a.remove();
                URL.revokeObjectURL(url);
            });
    };

    const remove = async (id) => {
        if (!window.confirm('Diese Vorlage wirklich löschen?')) return;
        await fetch(`${API}/api/templates/${id}`, { method: 'DELETE', headers: getHeaders() });
        fetchTemplates();
    };

    return (
        <div className="section" style={{ minHeight: 'calc(100vh - 140px)' }}>
            <div className="container">
                <Link to="/privat" className="icon-button" style={{ display: 'inline-flex', gap: '8px', alignItems: 'center', marginBottom: '32px' }}>
                    <ArrowLeft size={20} />
                    <span style={{ fontWeight: 500 }}>Zurück</span>
                </Link>

                <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                        <LayoutTemplate size={26} color="var(--accent)" />
                        <h1 className="section-title" style={{ marginBottom: 0 }}>Templates</h1>
                    </div>
                    <p className="section-subtitle" style={{ marginBottom: '32px' }}>
                        {templates.length} Vorlage{templates.length !== 1 ? 'n' : ''}
                    </p>

                    {/* Upload */}
                    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '24px 28px', marginBottom: '32px' }}>
                        <div style={{ fontSize: '16px', fontWeight: 700, marginBottom: '18px' }}>Neue Vorlage hochladen</div>

                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '16px' }}>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Titel</label>
                                <input value={title} onChange={e => setTitle(e.target.value)} style={inp} placeholder="z.B. Content-Editor Template" />
                            </div>
                            <div>
                                <label style={{ fontSize: '12px', fontWeight: 600, color: MUTED, display: 'block', marginBottom: '5px' }}>Beschreibung</label>
                                <input value={description} onChange={e => setDescription(e.target.value)} style={inp} placeholder="Optional" />
                            </div>
                        </div>

                        <label
                            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={e => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files?.[0]); }}
                            style={{
                                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                                gap: '10px', padding: '28px 20px', borderRadius: '12px', cursor: 'pointer',
                                border: `2px dashed ${dragOver ? 'var(--accent)' : 'var(--border)'}`,
                                background: dragOver ? 'rgba(251,146,60,0.06)' : 'var(--bg-secondary)',
                                transition: 'all 0.15s', marginBottom: '16px',
                            }}>
                            <input type="file" style={{ display: 'none' }} onChange={e => pickFile(e.target.files?.[0])} />
                            {selectedFile ? (
                                <>
                                    <FileArchive size={24} color="var(--accent)" />
                                    <div style={{ fontSize: '13px', fontWeight: 600 }}>{selectedFile.name}</div>
                                    <div style={{ fontSize: '11px', color: MUTED }}>{(selectedFile.size / 1024).toFixed(0)} KB — klicken zum Ändern</div>
                                </>
                            ) : (
                                <>
                                    <Upload size={24} color={MUTED} />
                                    <div style={{ fontSize: '13px', fontWeight: 600 }}>Datei oder ZIP hier ablegen</div>
                                    <div style={{ fontSize: '11px', color: MUTED }}>oder klicken zum Auswählen</div>
                                </>
                            )}
                        </label>

                        {uploadError && (
                            <div style={{ padding: '10px 14px', marginBottom: '14px', background: 'rgba(255,80,80,0.1)', border: '1px solid rgba(255,80,80,0.3)', borderRadius: '8px', color: '#ff5050', fontSize: '13px' }}>
                                {uploadError}
                            </div>
                        )}

                        <button onClick={upload} disabled={uploading} className="btn-primary">
                            {uploading ? 'Lädt hoch…' : 'Hochladen'}
                        </button>
                    </div>

                    {/* List */}
                    {templates.length === 0 ? (
                        <div style={{ padding: '48px', textAlign: 'center', color: MUTED, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px' }}>
                            Noch keine Vorlagen hochgeladen.
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <AnimatePresence>
                                {templates.map(t => (
                                    <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                                        style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap',
                                            background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: '12px', padding: '16px 20px' }}>
                                        <FileArchive size={22} color="var(--accent)" style={{ flexShrink: 0 }} />
                                        <div style={{ flex: 1, minWidth: '160px' }}>
                                            <div style={{ fontSize: '15px', fontWeight: 700 }}>{t.title}</div>
                                            {t.description && <div style={{ fontSize: '13px', color: MUTED, marginTop: '2px' }}>{t.description}</div>}
                                            <div style={{ fontSize: '11px', color: MUTED, marginTop: '4px' }}>
                                                {t.filename} · {t.created_at ? new Date(t.created_at).toLocaleDateString('de-DE') : ''}
                                            </div>
                                        </div>
                                        <button onClick={() => download(t)} className="icon-button" style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#60a5fa' }}>
                                            <Download size={16} /> Download
                                        </button>
                                        <button onClick={() => remove(t.id)} className="icon-button" style={{ color: '#ef4444' }}>
                                            <Trash2 size={16} />
                                        </button>
                                    </motion.div>
                                ))}
                            </AnimatePresence>
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
